import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../data/supabaseClient';
import { AuthContext } from '../Provider.js';
import { useToast } from '../hooks/ToastContext';
import Spinner from '../common/Spinner';
import UserInfo from './UserInfo';
import UserStats from './UserStats';
import UserBadges from './UserBadges';
import CarManager from './CarManager';
import ActivePosts from './ActivePosts';
import HistoryPosts from './HistoryPosts';
import DetailedRatings from '../DetailedRatings';
import './MyProfile.css';

const MyProfilePage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user, refreshProfile } = useContext(AuthContext);
  const { addToast } = useToast();
  
  // State variables
  const [userId, setUserId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [activePosts, setActivePosts] = useState([]);
  const [activePackages, setActivePackages] = useState([]);
  const [archivedPosts, setArchivedPosts] = useState([]);
  const [archivedPackages, setArchivedPackages] = useState([]);
  const [cars, setCars] = useState([]);
  const [stats, setStats] = useState(null);
  const [cancellationStats, setCancellationStats] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [activeTab, setActiveTab] = useState('stats');

  // Loading states
  const [profileLoading, setProfileLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(true);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [carsLoading, setCarsLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [cancellationStatsLoading, setCancellationStatsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Error state
  const [error, setError] = useState(null);

  // Helper function to refresh packages data
  const fetchPackages = async () => {
    if (!userId) return;
    
    try {
      setPackagesLoading(true);
      
      // Due to RLS policies, we need to handle active and archived packages separately
      // Active packages are visible to everyone
      const { data: activePackagesData, error: activePackagesError } = await supabase
        .from('packages')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active');
        
      if (activePackagesError) {
        console.error('Error fetching active packages:', activePackagesError.message);
        throw new Error(t('profile.error_fetch_packages'));
      }
      
      // For archived packages, we need to use the RPC function to bypass RLS
      // This is a workaround until the RLS policy is updated to allow users to see their own archived packages
      const { data: archivedPackagesData, error: archivedPackagesError } = await supabase
        .rpc('get_user_archived_packages', { user_id_param: userId });
        
      if (archivedPackagesError) {
        console.error('Error fetching archived packages:', archivedPackagesError.message);
        // Don't throw here, we'll just use a fallback
        // Fallback to empty array if RPC fails
        console.log('Using fallback for archived packages');
        setActivePackages(activePackagesData || []);
        // Get the archived packages from local state - this won't be accurate but it's better than nothing
        return;
      }
      
      console.log('Fetched active packages:', activePackagesData?.length || 0, 'items');
      console.log('Fetched archived packages:', archivedPackagesData?.length || 0, 'items');
      
      // Update state
      setActivePackages(activePackagesData || []);
      setArchivedPackages(archivedPackagesData || []);
      
    } catch (error) {
      console.error('Error in fetchPackages:', error);
      addToast({
        type: 'error',
        title: t('errors.packages_load_error'),
        description: error.message
      });
    } finally {
      setPackagesLoading(false);
    }
  };

  // Profile management functions
  const updateProfile = async (profileData) => {
    try {
      setActionLoading(true);
      setValidationErrors({});
      
      // Check username uniqueness if changing username
      if (profileData.username && profileData.username !== profile.username) {
        const { data: existingUsers, error: checkError } = await supabase
          .from('profiles')
          .select('id')
          .ilike('username', profileData.username)
          .neq('id', profile.id);
          
        if (checkError) throw checkError;
        
        if (existingUsers && existingUsers.length > 0) {
          setValidationErrors({ username: t('errors.username_taken') });
          return;
        }
      }
      
      // Update profile
      const { error } = await supabase
        .from('profiles')
        .update({ 
          ...profileData,
          updated_at: new Date().toISOString()
        })
        .eq('id', profile.id);

      if (error) throw error;
      
      // Update local state
      setProfile(prev => ({ ...prev, ...profileData }));
      
      // Refresh AuthContext so changes are reflected everywhere
      try {
        await refreshProfile();
        console.log('✅ Profile changes synced to AuthContext');
        
        // If country was changed, wait for React to re-render before dispatching event
        if (profileData.country_code) {
          // Give React time to update context (300ms should be enough)
          await new Promise(resolve => setTimeout(resolve, 300));
          
          console.log('🌍 Dispatching countryChanged event with:', profileData.country_code);
          window.dispatchEvent(new CustomEvent('countryChanged', {
            detail: {
              newCountry: profileData.country_code,
              message: `Country changed. Feed will show posts from ${profileData.country_code}.`
            }
          }));
        }
      } catch (error) {
        console.warn('⚠️ Could not refresh AuthContext:', error);
      }
      
      addToast({
        type: 'success',
        title: t('profile.profile_updated'),
        description: t('profile.profile_update_success')
      });
      
      return true;
    } catch (error) {
      console.error('Error updating profile:', error);
      
      addToast({
        type: 'error',
        title: t('errors.update_failed'),
        description: error.message
      });
      
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const updateAvatar = async (avatarSource, type = 'file') => {
    try {
      setActionLoading(true);
      
      let avatarUrl = null;
      
      // Handle different avatar source types
      if (type === 'file' && avatarSource) {
        // File upload
        const file = avatarSource;
        
        // Validate file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(t('errors.file_size_limit'));
        }

        // Generate unique filename
        const fileExt = file.name.split('.').pop();
        const fileName = `${uuidv4()}.${fileExt}`;
        const filePath = `avatars/${userId}/${fileName}`;

        // Upload image
        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl }, error: urlError } = supabase.storage
          .from('avatars')
          .getPublicUrl(filePath);

        if (urlError) throw urlError;
        
        avatarUrl = publicUrl;
      } else if (type === 'url') {
        // Use provided URL (for default avatars)
        avatarUrl = avatarSource;
      }
      // Removed 'remove' type - users should always have an avatar

      // Update avatar in profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          avatar_url: avatarUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (updateError) throw updateError;

      // Update local state
      setProfile(prev => ({
        ...prev,
        avatar_url: avatarUrl
      }));
      
      addToast({
        type: 'success',
        title: t('profile.avatar_updated'),
        description: t('profile.avatar_update_success')
      });
      
      return true;
    } catch (error) {
      console.error('Error updating avatar:', error);
      
      addToast({
        type: 'error',
        title: t('errors.update_failed'),
        description: error.message
      });
      
      return false;
    } finally {
      setActionLoading(false);
    }
  };
  
  // Car management functions
  const addCar = async (carData) => {
    try {
      setActionLoading(true);
      
      const { error } = await supabase
        .from('cars')
        .insert([{
          user_id: userId,
          brand: carData.brand,
          model: carData.model || null,
          year: carData.year || null,
          number: carData.number
        }]);
  
      if (error) throw error;
      
      // Refresh car list
      const { data: updatedCars, error: fetchError } = await supabase
        .from('cars')
        .select('*')
        .eq('user_id', userId);
        
      if (fetchError) throw fetchError;
      
      setCars(updatedCars || []);
      
      addToast({
        type: 'success',
        title: t('profile.car_added'),
        description: t('profile.car_add_success')
      });
      
      return true;
    } catch (error) {
      console.error('Error adding car:', error);
      
      addToast({
        type: 'error',
        title: t('errors.car_add_failed'),
        description: error.message
      });
      
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const deleteCar = async (carId) => {
    if (!window.confirm(t('profile.confirm_delete_car'))) {
      return false;
    }
    
    try {
      setActionLoading(true);
      
      const { error } = await supabase
        .from('cars')
        .delete()
        .eq('id', carId);
        
      if (error) throw error;
      
      // Update local state
      setCars(prev => prev.filter(car => car.id !== carId));
      
      addToast({
        type: 'success',
        title: t('profile.car_deleted'),
        description: t('profile.car_delete_success')
      });
      
      return true;
    } catch (error) {
      console.error('Error deleting car:', error);
      
      addToast({
        type: 'error',
        title: t('errors.car_delete_failed'),
        description: error.message
      });
      
      return false;
    } finally {
      setActionLoading(false);
    }
  };
  
  // Post management functions
  const deletePost = async (postId) => {
    try {
      setActionLoading(true);
      
      // First find the post to archive
      const postToArchive = activePosts.find(post => post.id === postId);
      if (!postToArchive) {
        throw new Error('Post not found');
      }
      
      // Update status to archived
      const { error } = await supabase
        .from('posts')
        .update({ status: 'archived' })
        .eq('id', postId);
          
      if (error) throw error;
      
      // Update local state
      setActivePosts(prev => prev.filter(post => post.id !== postId));
      setArchivedPosts(prev => [postToArchive, ...prev]);
      
      addToast({
        type: 'success',
        title: t('posts.post_archived'),
        description: t('posts.post_archive_success')
      });
      
      return true;
    } catch (error) {
      console.error('Error archiving post:', error);
      
      addToast({
        type: 'error',
        title: t('errors.archive_failed'),
        description: error.message
      });
      
      return false;
    } finally {
      setActionLoading(false);
    }
  };
  
  // Package management functions
  const deletePackage = async (packageId) => {
    try {
      setActionLoading(true);
      
      // First find the package to archive
      const packageToArchive = activePackages.find(pkg => pkg.id === packageId);
      if (!packageToArchive) {
        throw new Error('Package not found');
      }
      
      // Log the actions for debugging
      console.log('Archiving package:', packageId);
      console.log('Current user:', userId);
      console.log('Package owner:', packageToArchive.user_id);
      
      // Update local state optimistically first for better UX
      setActivePackages(prev => prev.filter(pkg => pkg.id !== packageId));
      setArchivedPackages(prev => [
        {
          ...packageToArchive,
          status: 'archived'
        },
        ...prev
      ]);
      
      // Try calling the RPC function to archive the package
      const { error: rpcError } = await supabase
        .rpc('archive_package', { p_package_id: packageId });
      
      if (rpcError) {
        console.error('Error with RPC archive_package:', rpcError);
        
        // Fallback to direct update if RPC fails
        try {
          console.log('RPC failed, trying direct update...');
          
          const { error: updateError } = await supabase
            .from('packages')
            .update({ 
              status: 'archived',
              updated_at: new Date().toISOString()
            })
            .eq('id', packageId);
            
          if (updateError) {
            console.error('Direct update also failed:', updateError);
            // Don't throw here, we already updated the UI optimistically
          } else {
            console.log('Direct update succeeded');
          }
        } catch (directUpdateError) {
          console.error('Exception during direct update:', directUpdateError);
          // Don't throw here either, we already updated the UI
        }
      } else {
        console.log('Package archived via RPC successfully');
      }
      
      // After archiving, check the status to confirm
      try {
        // This might fail due to RLS policies - that's expected
        const { data: checkData, error: checkError } = await supabase
          .from('packages')
          .select('id, status')
          .eq('id', packageId)
          .maybeSingle();
          
        if (checkError) {
          console.log('Could not check package status due to RLS:', checkError.message);
        } else {
          console.log('Current package status:', checkData?.status || 'unknown');
        }
      } catch (checkError) {
        console.error('Error checking package status:', checkError);
      }
      
      // Success notification
      addToast({
        type: 'success',
        title: t('posts.package_archived'),
        description: t('posts.package_archive_success')
      });
      
      return true;
    } catch (error) {
      console.error('Error archiving package:', error);
      
      // Revert the optimistic update
      fetchPackages();
      
      addToast({
        type: 'error',
        title: t('errors.archive_failed'),
        description: error.message
      });
      
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  // Fetch user profile, cars, posts, packages, and stats
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setProfileLoading(true);
        
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (userError) {
          console.error('Error fetching user:', userError.message);
          throw new Error(t('profile.error_fetch_user'));
        }
        
        if (!user) {
          navigate('/login');
          return;
        }
        
        setUserId(user.id);
        
        // Fetch profile
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
          
        if (profileError) {
          console.error('Error fetching profile:', profileError.message);
          throw new Error(t('profile.error_fetch_profile'));
        }
        
        setProfile(profileData);
        
      } catch (error) {
        console.error('Error in fetchProfileData:', error);
        setError(error.message);
        addToast({
          type: 'error',
          title: t('errors.profile_load_error'),
          description: error.message
        });
      } finally {
        setProfileLoading(false);
      }
    };
    
    const fetchCars = async () => {
      if (!userId) return;
      
      try {
        setCarsLoading(true);
        
        const { data: carsData, error: carsError } = await supabase
          .from('cars')
          .select('*')
          .eq('user_id', userId);
          
        if (carsError) {
          console.error('Error fetching cars:', carsError.message);
          throw new Error(t('profile.error_fetch_cars'));
        }
        
        setCars(carsData || []);
        
      } catch (error) {
        console.error('Error in fetchCars:', error);
        addToast({
          type: 'error',
          title: t('errors.cars_load_error'),
          description: error.message
        });
      } finally {
        setCarsLoading(false);
      }
    };
    
    const fetchPosts = async () => {
      if (!userId) return;
      
      try {
        setPostsLoading(true);
        
        // Get all posts for this user - active and archived
        const { data: postsData, error: postsError } = await supabase
          .from('posts')
          .select('*')
          .eq('user_id', userId);
          
        if (postsError) {
          console.error('Error fetching posts:', postsError.message);
          throw new Error(t('profile.error_fetch_posts'));
        }
        
        // Filter posts by status
        const active = postsData.filter(post => post.status === 'active') || [];
        const archived = postsData.filter(post => post.status === 'archived' || post.status === 'completed') || [];
        
        setActivePosts(active);
        setArchivedPosts(archived);
        
      } catch (error) {
        console.error('Error in fetchPosts:', error);
        addToast({
          type: 'error',
          title: t('errors.posts_load_error'),
          description: error.message
        });
      } finally {
        setPostsLoading(false);
      }
    };

    const fetchStats = async () => {
      if (!userId) return;
      
      try {
        setStatsLoading(true);
        
        const { data: statsData, error: statsError } = await supabase
          .from('user_stats')
          .select('*')
          .eq('user_id', userId)
          .single();
          
        if (statsError && statsError.code !== 'PGRST116') {
          console.error('Error fetching stats:', statsError.message);
          throw new Error(t('profile.error_fetch_stats'));
        }
        
        // Set sensible defaults if no stats found
        const defaultStats = {
          user_id: userId,
          total_distance_offered_km: 0,
          total_distance_shared_km: 0,
          total_rides_offered: 0,
          total_rides_shared: 0,
          total_reviews: 0,
          average_rating: 0
        };
        
        const finalStats = statsData || defaultStats;
        setStats(finalStats);
        
      } catch (error) {
        console.error('Error in fetchStats:', error);
        // Don't show a toast for stats errors as this is non-critical
      } finally {
        setStatsLoading(false);
      }
    };

    const fetchCancellationStats = async () => {
      if (!userId) return;
      
      try {
        setCancellationStatsLoading(true);
        
        const { data: cancellationData, error: cancellationError } = await supabase
          .rpc('get_user_cancellation_stats', { p_user_id: userId });
          
        if (cancellationError) {
          console.error('Error fetching cancellation stats:', cancellationError.message);
          // Don't throw here, cancellation stats are optional
          setCancellationStats(null);
        } else {
          setCancellationStats(cancellationData);
        }
        
      } catch (error) {
        console.error('Error in fetchCancellationStats:', error);
        setCancellationStats(null);
      } finally {
        setCancellationStatsLoading(false);
      }
    };

    fetchProfileData();
    
    if (userId) {
      fetchCars();
      fetchPosts();
      fetchPackages();
      fetchStats();
      fetchCancellationStats();
    }
  }, [userId, navigate, t, addToast]);

  // Calculate loadings
  const isLoading = profileLoading;
  const isContentLoading = postsLoading || packagesLoading || carsLoading || statsLoading || cancellationStatsLoading;
  
  if (isLoading) {
    return (
      <>
        <div className="loading-container">
          <Spinner size="large" customText={t('app.loading')} />
        </div>
      </>
    );
  }

  const TABS = [
    { id: 'stats', label: t('profile.tab_stats') },
    { id: 'posts', label: t('profile.tab_posts') },
    { id: 'badges', label: t('profile.tab_badges') },
    { id: 'cars', label: t('profile.tab_cars') },
    { id: 'ratings', label: t('profile.tab_ratings') },
  ];

  return (
    <>
      <div className="Myprofile-container">
        {/* User info section (persistent header) */}
        <UserInfo
          profile={profile}
          onUpdateProfile={updateProfile}
          onUpdateAvatar={updateAvatar}
          validationErrors={validationErrors}
          isLoading={actionLoading}
        />

        {/* Section tabs */}
        <div className="profile-tabs" role="tablist">
          {TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`profile-tab${activeTab === tab.id ? ' active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Active section panel */}
        <div className="profile-tab-panel" role="tabpanel">
          {activeTab === 'stats' && (
            <UserStats
              stats={stats}
              activePosts={activePosts}
              activePackages={activePackages}
              archivedPosts={archivedPosts}
              archivedPackages={archivedPackages}
              cancellationStats={cancellationStats}
              isLoading={isContentLoading}
            />
          )}

          {activeTab === 'posts' && (
            <div className="Post-box">
              <ActivePosts
                activePosts={activePosts}
                activePackages={activePackages}
                onDeletePost={deletePost}
                onDeletePackage={deletePackage}
                isLoading={postsLoading || packagesLoading || actionLoading}
              />
              <HistoryPosts
                archivedPosts={archivedPosts}
                archivedPackages={archivedPackages}
                isLoading={postsLoading || packagesLoading}
              />
            </div>
          )}

          {activeTab === 'badges' && <UserBadges userId={userId} />}

          {activeTab === 'cars' && (
            <CarManager
              cars={cars}
              onAddCar={addCar}
              onDeleteCar={deleteCar}
              isLoading={actionLoading || carsLoading}
            />
          )}

          {activeTab === 'ratings' && <DetailedRatings userId={userId} />}
        </div>
      </div>
    </>
  );
};

export default MyProfilePage;